import { KazokugotoService } from '@/services';
import { ENDPOINT } from '@/services/endpoint';
import { IResponseError } from '@/types/common';
import { IKazokugotoCharacter, IKazokugotoDuo } from '@/types/response';
import { notificationMessage, NOTIFY_STATUS } from '@/utils';
import { PlusOutlined, UploadOutlined } from '@ant-design/icons';
import {
  Alert,
  Avatar,
  Button,
  Card,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Table,
  TableColumnsType,
  Tag,
  Typography,
  Upload,
} from 'antd';
import type { UploadProps } from 'antd';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useMutation, useQuery, useQueryClient } from 'react-query';

const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;

const DOW_LABELS = ['日', '月', '火', '水', '木', '金', '土'];

interface IProps {
  title?: string;
}

export const KazokugotoCharacterList: React.FC<IProps> = ({ title }) => {
  const queryClient = useQueryClient();
  const [editTarget, setEditTarget] = useState<IKazokugotoCharacter | null>(
    null,
  );
  const [editPersonality, setEditPersonality] = useState<string>('');

  // 新規追加モーダル
  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createPersonality, setCreatePersonality] = useState('');

  const { data, isFetching } = useQuery(
    [ENDPOINT.KAZOKUGOTO.LIST_CHARACTERS],
    () => KazokugotoService.listCharacters(),
  );

  const updateMutation = useMutation(
    (params: { id: number; personality: string }) =>
      KazokugotoService.updateCharacter(params.id, {
        personality: params.personality,
      }),
    {
      onSuccess: () => {
        notificationMessage({
          message: 'personality を更新しました',
          type: NOTIFY_STATUS.SUCCESS,
        });
        setEditTarget(null);
        queryClient.invalidateQueries([ENDPOINT.KAZOKUGOTO.LIST_CHARACTERS]);
      },
      onError: (error: IResponseError) => {
        notificationMessage({
          message: `更新に失敗: ${error.message ?? 'unknown error'}`,
          type: NOTIFY_STATUS.ERROR,
        });
      },
    },
  );

  const createMutation = useMutation(
    (body: { name: string; personality: string }) =>
      KazokugotoService.createCharacter(body),
    {
      onSuccess: () => {
        notificationMessage({
          message: 'キャラクタを追加しました。アバターはカードから設定してください',
          type: NOTIFY_STATUS.SUCCESS,
        });
        setCreateOpen(false);
        setCreateName('');
        setCreatePersonality('');
        queryClient.invalidateQueries([ENDPOINT.KAZOKUGOTO.LIST_CHARACTERS]);
      },
      onError: (error: IResponseError) => {
        notificationMessage({
          message: `追加に失敗: ${error.message ?? 'unknown error'}`,
          type: NOTIFY_STATUS.ERROR,
        });
      },
    },
  );

  const uploadAvatarMutation = useMutation(
    (params: { id: number; file: File }) =>
      KazokugotoService.uploadCharacterAvatar(params.id, params.file),
    {
      onSuccess: () => {
        notificationMessage({
          message: 'アバター画像を差し替えました',
          type: NOTIFY_STATUS.SUCCESS,
        });
        queryClient.invalidateQueries([ENDPOINT.KAZOKUGOTO.LIST_CHARACTERS]);
      },
      onError: (error: IResponseError) => {
        notificationMessage({
          message: `アップロードに失敗: ${error.message ?? 'unknown error'}`,
          type: NOTIFY_STATUS.ERROR,
        });
      },
    },
  );

  // 曜日別コンビ
  const { data: duosData, isFetching: isFetchingDuos } = useQuery(
    [ENDPOINT.KAZOKUGOTO.LIST_DUOS],
    () => KazokugotoService.listDuos(),
  );

  const updateDuoMutation = useMutation(
    (params: {
      dayOfWeek: number;
      body: { bokeCharacterId?: number; tsukkomiCharacterId?: number };
    }) => KazokugotoService.updateDuo(params.dayOfWeek, params.body),
    {
      onSuccess: () => {
        notificationMessage({
          message: 'コンビを更新しました',
          type: NOTIFY_STATUS.SUCCESS,
        });
        queryClient.invalidateQueries([ENDPOINT.KAZOKUGOTO.LIST_DUOS]);
        // characters 側の出演曜日タグも変わるので再取得
        queryClient.invalidateQueries([ENDPOINT.KAZOKUGOTO.LIST_CHARACTERS]);
      },
      onError: (error: IResponseError) => {
        notificationMessage({
          message: `コンビ更新に失敗: ${error.message ?? 'unknown error'}`,
          type: NOTIFY_STATUS.ERROR,
        });
      },
    },
  );

  const characterOptions = (data?.data ?? []).map((c) => ({
    value: c.id,
    label: c.name,
  }));

  const duoColumns: TableColumnsType<IKazokugotoDuo> = [
    {
      title: '曜日',
      dataIndex: 'dayOfWeek',
      width: 80,
      render: (d: number) => <Tag>{DOW_LABELS[d]}曜</Tag>,
    },
    {
      title: 'ボケ',
      width: 220,
      render: (_, record) => (
        <Select
          value={record.bokeCharacterId}
          style={{ width: 200 }}
          options={characterOptions}
          loading={
            updateDuoMutation.isLoading &&
            updateDuoMutation.variables?.dayOfWeek === record.dayOfWeek
          }
          onChange={(v) =>
            updateDuoMutation.mutate({
              dayOfWeek: record.dayOfWeek,
              body: { bokeCharacterId: v },
            })
          }
        />
      ),
    },
    {
      title: 'ツッコミ',
      width: 220,
      render: (_, record) => (
        <Select
          value={record.tsukkomiCharacterId}
          style={{ width: 200 }}
          options={characterOptions}
          loading={
            updateDuoMutation.isLoading &&
            updateDuoMutation.variables?.dayOfWeek === record.dayOfWeek
          }
          onChange={(v) =>
            updateDuoMutation.mutate({
              dayOfWeek: record.dayOfWeek,
              body: { tsukkomiCharacterId: v },
            })
          }
        />
      ),
    },
  ];

  const handleStartEdit = (character: IKazokugotoCharacter) => {
    setEditTarget(character);
    setEditPersonality(character.personality);
  };

  const handleSaveEdit = () => {
    if (!editTarget) return;
    if (!editPersonality.trim()) {
      notificationMessage({
        message: 'personality は必須です',
        type: NOTIFY_STATUS.WARNING,
      });
      return;
    }
    updateMutation.mutate({
      id: editTarget.id,
      personality: editPersonality,
    });
  };

  const handleCreate = () => {
    if (!createName.trim()) {
      notificationMessage({
        message: 'キャラクタ名は必須です',
        type: NOTIFY_STATUS.WARNING,
      });
      return;
    }
    if (!createPersonality.trim()) {
      notificationMessage({
        message: 'personality は必須です',
        type: NOTIFY_STATUS.WARNING,
      });
      return;
    }
    createMutation.mutate({
      name: createName.trim(),
      personality: createPersonality,
    });
  };

  const beforeUpload =
    (characterId: number): UploadProps['beforeUpload'] =>
    (file) => {
      const isImage =
        file.type === 'image/png' ||
        file.type === 'image/jpeg' ||
        file.type === 'image/jpg';
      if (!isImage) {
        notificationMessage({
          message: 'PNG / JPEG のみ対応しています',
          type: NOTIFY_STATUS.ERROR,
        });
        return Upload.LIST_IGNORE;
      }
      if (file.size > 5 * 1024 * 1024) {
        notificationMessage({
          message: 'ファイルサイズは 5MB 以下にしてください',
          type: NOTIFY_STATUS.ERROR,
        });
        return Upload.LIST_IGNORE;
      }
      Modal.confirm({
        title: 'アバター画像を差し替えますか？',
        content: `${file.name} (${(file.size / 1024).toFixed(1)} KB)`,
        okText: 'アップロード',
        cancelText: 'キャンセル',
        onOk: () =>
          uploadAvatarMutation.mutate({ id: characterId, file: file as File }),
      });
      return false; // antd Upload に自動 POST させない
    };

  const renderCharacterCard = (c: IKazokugotoCharacter) => {
    const isUploadingThis =
      uploadAvatarMutation.isLoading &&
      uploadAvatarMutation.variables?.id === c.id;
    return (
      <Card
        key={c.id}
        size='small'
        style={{
          height: '100%',
          background: '#fff',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
          border: '1px solid #e5e7eb',
        }}
        styles={{
          header: { background: '#fff' },
          body: { background: '#fff' },
        }}
        title={
          <Space>
            <Text strong>{c.name}</Text>
            <Text type='secondary' style={{ fontSize: 12 }}>
              id={c.id}
            </Text>
          </Space>
        }
        extra={
          <Button size='small' onClick={() => handleStartEdit(c)}>
            personality 編集
          </Button>
        }
      >
        <div style={{ textAlign: 'center', marginBottom: 12 }}>
          {c.avatarUrl ? (
            <Avatar
              src={c.avatarUrl}
              size={96}
              alt={c.name}
              style={{
                background: '#f5f5f5',
                border: '1px solid #e5e7eb',
              }}
            />
          ) : (
            <Avatar
              size={96}
              style={{
                background: '#fafafa',
                border: '1px solid #e5e7eb',
                color: '#666',
              }}
            >
              {c.name.slice(0, 2)}
            </Avatar>
          )}
          <div style={{ marginTop: 8 }}>
            <Upload
              accept='image/png,image/jpeg'
              showUploadList={false}
              beforeUpload={beforeUpload(c.id)}
            >
              <Button
                size='small'
                icon={<UploadOutlined />}
                loading={isUploadingThis}
              >
                アバター差替
              </Button>
            </Upload>
          </div>
        </div>

        <div style={{ marginBottom: 8 }}>
          <Text strong style={{ fontSize: 12, display: 'block' }}>
            出演曜日
          </Text>
          <Space wrap size={[4, 4]} style={{ marginTop: 4 }}>
            {c.dowAsBoke.length === 0 && c.dowAsTsukkomi.length === 0 && (
              <Text type='secondary' style={{ fontSize: 12 }}>
                未登録
              </Text>
            )}
            {c.dowAsBoke.map((d) => (
              <Tag key={`b-${d}`} color='magenta'>
                {DOW_LABELS[d]}（ボケ）
              </Tag>
            ))}
            {c.dowAsTsukkomi.map((d) => (
              <Tag key={`t-${d}`} color='cyan'>
                {DOW_LABELS[d]}（ツッコミ）
              </Tag>
            ))}
          </Space>
        </div>

        <div>
          <Text strong style={{ fontSize: 12, display: 'block' }}>
            personality
          </Text>
          <Paragraph
            style={{
              marginTop: 4,
              marginBottom: 0,
              fontSize: 13,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {c.personality}
          </Paragraph>
        </div>
      </Card>
    );
  };

  return (
    <div>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <div className='flex flex-col gap-[16px] mx-auto w-full max-w-[1280px] h-full'>
        <header>
          <Row justify='space-between' align='top' wrap={false}>
            <Col>
              <Title level={3} style={{ marginTop: 0 }}>
                かぞくごと。キャラクター
              </Title>
              <Paragraph type='secondary'>
                漫才生成で使われるキャラクタの一覧です。personality（性格・口調）の変更は次回以降の生成プロンプトに反映されます。
              </Paragraph>
            </Col>
            <Col>
              <Button
                type='primary'
                icon={<PlusOutlined />}
                onClick={() => setCreateOpen(true)}
              >
                キャラを追加
              </Button>
            </Col>
          </Row>
        </header>

        <Alert
          type='info'
          showIcon
          message='アバター画像はサムネ生成時の参照画像として使われます。差し替えた場合、次回以降の漫才サムネが新しい画像をベースに生成されます。'
        />

        <Spin spinning={isFetching}>
          <Row gutter={[16, 16]}>
            {(data?.data ?? []).map((c) => (
              <Col xs={24} sm={12} lg={8} xl={6} key={c.id}>
                {renderCharacterCard(c)}
              </Col>
            ))}
          </Row>
        </Spin>

        <Card
          title='曜日別コンビ'
          size='small'
          style={{
            background: '#fff',
            border: '1px solid #e5e7eb',
            boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
          }}
          styles={{ header: { background: '#fff' }, body: { background: '#fff' } }}
        >
          <Paragraph type='secondary' style={{ marginTop: 0 }}>
            各曜日のボケ・ツッコミを差し替えると、その曜日に生成される漫才のコンビが変わります。
          </Paragraph>
          <Table<IKazokugotoDuo>
            rowKey='id'
            loading={isFetchingDuos}
            columns={duoColumns}
            dataSource={duosData?.data ?? []}
            pagination={false}
            scroll={{ x: 560 }}
          />
        </Card>
      </div>

      {/* personality 編集モーダル */}
      <Modal
        title={editTarget ? `personality 編集: ${editTarget.name}` : ''}
        open={!!editTarget}
        onCancel={() => setEditTarget(null)}
        onOk={handleSaveEdit}
        confirmLoading={updateMutation.isLoading}
        okText='保存'
        cancelText='キャンセル'
        width={700}
      >
        <Form layout='vertical'>
          <Form.Item label='personality' required>
            <TextArea
              value={editPersonality}
              onChange={(e) => setEditPersonality(e.target.value)}
              autoSize={{ minRows: 6, maxRows: 16 }}
              maxLength={2000}
              showCount
              placeholder='性格・口調を記述。例：「天然。明るい。マイペース。口調「えへへ」「大丈夫だと思った！」」'
              style={{ fontFamily: 'monospace', fontSize: 13 }}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 新規キャラ追加モーダル */}
      <Modal
        title='キャラクタを追加'
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={handleCreate}
        confirmLoading={createMutation.isLoading}
        okText='追加'
        cancelText='キャンセル'
        width={700}
      >
        <Alert
          type='info'
          showIcon
          style={{ marginBottom: 16 }}
          message='アバター画像は追加後にカードの「アバター差替」から設定してください。コンビ（曜日割当）は下部の「曜日別コンビ」で設定します。'
        />
        <Form layout='vertical'>
          <Form.Item label='キャラクタ名' required>
            <Input
              value={createName}
              onChange={(e) => setCreateName(e.target.value)}
              maxLength={100}
              showCount
              placeholder='例：ハナちゃん'
            />
          </Form.Item>
          <Form.Item label='personality' required>
            <TextArea
              value={createPersonality}
              onChange={(e) => setCreatePersonality(e.target.value)}
              autoSize={{ minRows: 6, maxRows: 16 }}
              maxLength={2000}
              showCount
              placeholder='性格・口調を記述。例：「天然。明るい。マイペース。口調「えへへ」「大丈夫だと思った！」」'
              style={{ fontFamily: 'monospace', fontSize: 13 }}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
